import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  DeleteForever as DeleteIcon,
  Search as SearchIcon,
  FolderOpen as FolderIcon,
  Description as FileIcon,
  Storage as StorageIcon,
  InsertDriveFile as DocIcon,
  EventNote as DateIcon,
  WarningAmber as WarningIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getGeneratedReportsList,
  deleteGeneratedReport,
  downloadGeneratedReport,
} from 'Report/Domain/Services/ReportService';
import {
  MODERN_COLORS,
  MODERN_SHADOWS,
  MODERN_RADIUS,
} from 'UI/Application/Components/ModernLayout/tokens';

export default function AdminReportsView() {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    totalReports: 0,
    totalSizeBytes: 0,
    totalSizeFormatted: '0 B',
    directory: 'public/reports',
  });

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [extensionFilter, setExtensionFilter] = useState('ALL');

  // Deletion Modal
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await getGeneratedReportsList();
      if (response && response.success) {
        setReports(response.data || []);
        if (response.summary) {
          setSummary(response.summary);
        }
      } else {
        setReports([]);
      }
    } catch (err) {
      toast.error('No se pudo cargar el listado de informes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // Unique categories and extensions for filters
  const categories = useMemo(() => {
    const set = new Set();
    reports.forEach((r) => {
      if (r.category) set.add(r.category);
    });
    return Array.from(set).sort();
  }, [reports]);

  const extensions = useMemo(() => {
    const set = new Set();
    reports.forEach((r) => {
      if (r.extension) set.add(r.extension.toUpperCase());
    });
    return Array.from(set).sort();
  }, [reports]);

  // Filtered reports
  const filteredReports = useMemo(() => {
    return reports.filter((r) => {
      const matchSearch =
        !search ||
        r.filename.toLowerCase().includes(search.toLowerCase()) ||
        (r.title && r.title.toLowerCase().includes(search.toLowerCase())) ||
        (r.category && r.category.toLowerCase().includes(search.toLowerCase()));

      const matchCategory =
        categoryFilter === 'ALL' || r.category === categoryFilter;

      const matchExtension =
        extensionFilter === 'ALL' ||
        (r.extension && r.extension.toUpperCase() === extensionFilter);

      return matchSearch && matchCategory && matchExtension;
    });
  }, [reports, search, categoryFilter, extensionFilter]);

  const handleDownload = async (filename) => {
    setDownloadingFile(filename);
    try {
      await downloadGeneratedReport(filename);
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;
    setDeleting(true);
    try {
      const resp = await deleteGeneratedReport(reportToDelete.filename);
      if (resp && resp.success) {
        toast.success(resp.message || 'Informe eliminado correctamente.');
        setReportToDelete(null);
        await fetchReports();
      } else {
        toast.error((resp && resp.message) || 'No se pudo eliminar el informe.');
      }
    } catch (err) {
      toast.error('Error al intentar eliminar el archivo.');
    } finally {
      setDeleting(false);
    }
  };

  const renderExtensionBadge = (ext) => {
    const upper = (ext || '').toUpperCase();
    let bg = MODERN_COLORS.primaryIce2;
    let color = MODERN_COLORS.primaryDark;
    let icon = <FileIcon sx={{ fontSize: 16, mr: 0.5 }} />;

    if (upper === 'XLSX' || upper === 'XLS' || upper === 'CSV') {
      bg = '#ecfdf5';
      color = '#059669';
      icon = <ExcelIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    } else if (upper === 'PDF') {
      bg = '#fef2f2';
      color = '#dc2626';
      icon = <PdfIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    }

    return (
      <Chip
        icon={icon}
        label={upper || 'FILE'}
        size="small"
        sx={{
          bgcolor: bg,
          color: color,
          fontWeight: 700,
          fontSize: '0.75rem',
          borderRadius: MODERN_RADIUS.pill,
          border: `1px solid ${color}33`,
        }}
      />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Box sx={{ maxWidth: 1380, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header Banner */}
      <Card
        sx={{
          mb: 3,
          borderRadius: MODERN_RADIUS.card,
          background: `linear-gradient(135deg, ${MODERN_COLORS.bgCard} 0%, ${MODERN_COLORS.primaryIce} 100%)`,
          border: `1.5px solid ${MODERN_COLORS.borderSoft}`,
          boxShadow: MODERN_SHADOWS.card,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip
                  icon={<FolderIcon sx={{ fontSize: '1rem !important', color: `${MODERN_COLORS.primaryDark} !important` }} />}
                  label="Administración / Repositorio de Documentos"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    bgcolor: MODERN_COLORS.primaryIce2,
                    color: MODERN_COLORS.primaryDark,
                    border: `1px solid ${MODERN_COLORS.borderSoft}`,
                    py: 0.5,
                  }}
                />
                <Chip
                  label={summary.directory}
                  size="small"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    bgcolor: 'white',
                    color: MODERN_COLORS.textMuted,
                    border: `1px solid ${MODERN_COLORS.borderLight}`,
                  }}
                />
              </Stack>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: MODERN_COLORS.textMain,
                  letterSpacing: '-0.02em',
                  mb: 1,
                }}
              >
                Informes Generados
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: MODERN_COLORS.textMuted,
                  maxWidth: 750,
                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  lineHeight: 1.5,
                }}
              >
                Directorio centralizado de todos los informes y exports generados por la intranet (organigrama, puestos de trabajo, volcados de personal y reportes operativos). Puedes auditar los archivos existentes, descargarlos directamente o eliminarlos para mantener el almacenamiento limpio.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                variant="contained"
                onClick={fetchReports}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
                sx={{
                  borderRadius: MODERN_RADIUS.pill,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  bgcolor: MODERN_COLORS.primary,
                  boxShadow: MODERN_SHADOWS.buttonPrimary,
                  '&:hover': {
                    bgcolor: MODERN_COLORS.primaryDark,
                  },
                }}
              >
                Actualizar Listado
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: MODERN_RADIUS.card,
              border: `1px solid ${MODERN_COLORS.borderLight}`,
              boxShadow: MODERN_SHADOWS.card,
              bgcolor: 'white',
              p: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: MODERN_COLORS.primaryIce2,
                  color: MODERN_COLORS.primaryDark,
                }}
              >
                <DocIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted, fontWeight: 600 }}>
                  INFORMES DISPONIBLES
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: MODERN_COLORS.textMain }}>
                  {summary.totalReports}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: MODERN_RADIUS.card,
              border: `1px solid ${MODERN_COLORS.borderLight}`,
              boxShadow: MODERN_SHADOWS.card,
              bgcolor: 'white',
              p: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#ecfdf5',
                  color: '#059669',
                }}
              >
                <StorageIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted, fontWeight: 600 }}>
                  ESPACIO TOTAL
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: MODERN_COLORS.textMain }}>
                  {summary.totalSizeFormatted}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: MODERN_RADIUS.card,
              border: `1px solid ${MODERN_COLORS.borderLight}`,
              boxShadow: MODERN_SHADOWS.card,
              bgcolor: 'white',
              p: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#eff6ff',
                  color: '#2563eb',
                }}
              >
                <CategoryIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted, fontWeight: 600 }}>
                  CATEGORÍAS ACTIVAS
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: MODERN_COLORS.textMain }}>
                  {categories.length}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: MODERN_RADIUS.card,
              border: `1px solid ${MODERN_COLORS.borderLight}`,
              boxShadow: MODERN_SHADOWS.card,
              bgcolor: 'white',
              p: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fff7ed',
                  color: '#ea580c',
                }}
              >
                <DateIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted, fontWeight: 600 }}>
                  ÚLTIMO REGISTRO
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: MODERN_COLORS.textMain, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {reports[0] ? formatDate(reports[0].createdAt) : 'Sin datos'}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Search Bar */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          borderRadius: MODERN_RADIUS.card,
          border: `1px solid ${MODERN_COLORS.borderLight}`,
          boxShadow: MODERN_SHADOWS.card,
          bgcolor: 'white',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nombre de archivo o título..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: MODERN_COLORS.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: MODERN_RADIUS.pill,
                },
              }}
            />
          </Grid>

          <Grid item xs={6} md={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-filter-label">Categoría</InputLabel>
              <Select
                labelId="category-filter-label"
                value={categoryFilter}
                label="Categoría"
                onChange={(e) => setCategoryFilter(e.target.value)}
                sx={{ borderRadius: MODERN_RADIUS.pill }}
              >
                <MenuItem value="ALL">Todas las categorías</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="ext-filter-label">Formato</InputLabel>
              <Select
                labelId="ext-filter-label"
                value={extensionFilter}
                label="Formato"
                onChange={(e) => setExtensionFilter(e.target.value)}
                sx={{ borderRadius: MODERN_RADIUS.pill }}
              >
                <MenuItem value="ALL">Todos los formatos</MenuItem>
                {extensions.map((ext) => (
                  <MenuItem key={ext} value={ext}>
                    {ext}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Table Container */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: MODERN_RADIUS.table,
          border: `1px solid ${MODERN_COLORS.borderLight}`,
          boxShadow: MODERN_SHADOWS.table,
          overflow: 'hidden',
        }}
      >
        <Table sx={{ minWidth: 700 }}>
          <TableHead sx={{ bgcolor: MODERN_COLORS.bgHeader }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: MODERN_COLORS.textMain, py: 2 }}>
                INFORME / ARCHIVO
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: MODERN_COLORS.textMain, py: 2 }}>
                CATEGORÍA
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: MODERN_COLORS.textMain, py: 2 }}>
                FORMATO
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: MODERN_COLORS.textMain, py: 2 }}>
                TAMAÑO
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: MODERN_COLORS.textMain, py: 2 }}>
                FECHA GENERACIÓN
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: MODERN_COLORS.textMain, py: 2 }}>
                ACCIONES
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} sx={{ color: MODERN_COLORS.primary, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: MODERN_COLORS.textMuted }}>
                    Cargando informes generados...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box sx={{ maxWidth: 360, mx: 'auto', textAlign: 'center' }}>
                    <FolderIcon sx={{ fontSize: 48, color: MODERN_COLORS.textSubtle, mb: 1.5 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MODERN_COLORS.textMain }}>
                      No se encontraron informes
                    </Typography>
                    <Typography variant="body2" sx={{ color: MODERN_COLORS.textMuted, mt: 0.5 }}>
                      {reports.length === 0
                        ? 'Actualmente no hay informes en la carpeta de reportes generados.'
                        : 'No hay ningún informe que coincida con los filtros seleccionados.'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredReports.map((report) => (
                <TableRow
                  key={report.filename}
                  sx={{
                    '&:hover': {
                      bgcolor: MODERN_COLORS.primaryIce,
                    },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <TableCell sx={{ py: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: MODERN_COLORS.primaryIce2,
                          color: MODERN_COLORS.primaryDark,
                          flexShrink: 0,
                        }}
                      >
                        <FileIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: MODERN_COLORS.textMain,
                            lineHeight: 1.2,
                          }}
                        >
                          {report.title || report.filename}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            color: MODERN_COLORS.textMuted,
                            fontSize: '0.75rem',
                            display: 'block',
                            mt: 0.3,
                          }}
                        >
                          {report.filename}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell sx={{ py: 2 }}>
                    <Chip
                      label={report.category || 'General'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        bgcolor: MODERN_COLORS.bgHeader,
                        color: MODERN_COLORS.textMuted,
                        border: `1px solid ${MODERN_COLORS.borderLight}`,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ py: 2 }}>
                    {renderExtensionBadge(report.extension)}
                  </TableCell>

                  <TableCell sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: MODERN_COLORS.textMain }}>
                      {report.sizeFormatted}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ color: MODERN_COLORS.textMain }}>
                      {formatDate(report.createdAt)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ py: 2 }}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Descargar informe">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(report.filename)}
                            disabled={downloadingFile === report.filename}
                            sx={{
                              color: MODERN_COLORS.primaryDark,
                              bgcolor: MODERN_COLORS.primaryIce2,
                              '&:hover': {
                                bgcolor: MODERN_COLORS.primary,
                                color: 'white',
                              },
                            }}
                          >
                            {downloadingFile === report.filename ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Eliminar informe">
                        <IconButton
                          size="small"
                          onClick={() => setReportToDelete(report)}
                          sx={{
                            color: '#dc2626',
                            bgcolor: '#fef2f2',
                            '&:hover': {
                              bgcolor: '#dc2626',
                              color: 'white',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Modal */}
      <Dialog
        open={Boolean(reportToDelete)}
        onClose={() => !deleting && setReportToDelete(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: MODERN_RADIUS.dialog,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
            }}
          >
            <WarningIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: MODERN_COLORS.textMain }}>
              ¿Eliminar este informe?
            </Typography>
            <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted }}>
              Acción destructiva permanente
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          {reportToDelete && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                El archivo se eliminará del disco en <strong>{summary.directory}</strong>. Esta acción no se puede deshacer.
              </Alert>

              <Box
                sx={{
                  bgcolor: MODERN_COLORS.bgHeader,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${MODERN_COLORS.borderLight}`,
                }}
              >
                <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted, display: 'block' }}>
                  ARCHIVO A ELIMINAR:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace', my: 0.5 }}>
                  {reportToDelete.filename}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted }}>
                    Tamaño: <strong>{reportToDelete.sizeFormatted}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ color: MODERN_COLORS.textMuted }}>
                    Fecha: <strong>{formatDate(reportToDelete.createdAt)}</strong>
                  </Typography>
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setReportToDelete(null)}
            disabled={deleting}
            variant="outlined"
            sx={{
              borderRadius: MODERN_RADIUS.pill,
              fontWeight: 600,
              borderColor: MODERN_COLORS.borderLight,
              color: MODERN_COLORS.textMain,
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleConfirmDelete}
            disabled={deleting}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{
              borderRadius: MODERN_RADIUS.pill,
              fontWeight: 700,
              bgcolor: '#dc2626',
              '&:hover': {
                bgcolor: '#b91c1c',
              },
            }}
          >
            {deleting ? 'Eliminando...' : 'Eliminar Informe'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
